import type { IBaseComponentProps } from 'components/base/types'
import type { IInputOption } from 'components/form/input/types'
import { computed, defineComponent, ref, type CSSProperties, type Ref } from 'vue'
import { MAX_VISIBLE_ITEMS, type SearchDropdownConfig, type SearchDropdownState } from '../../types'
import { toString } from '@/utils/formatting'
import Icon from 'components/icon'
import Centered from 'components/centered'
import { useElementBounding, useElementVisibility, useWindowSize } from '@vueuse/core'
import { VEC2 } from '@/utils/math/vector'
// import BasicInput from 'components/form/basic-input'
import type { InputSearchOptions } from 'components/form/input/search'
import type { FormFieldOptions } from 'components/form/types/fieldOptions'
import Button from 'components/button'
import AddIcon from 'assets/img/svg/icons/plus.svg?url'
import EmptyState from 'components/empty-state'
import SearchIcon from 'assets/img/svg/icons/search.svg?url'
import type { FormFieldRef } from 'components/form/types/field'
import { defineVariants } from '@/utils/style/variant'
import { FORM_INPUT_HEIGHT_PX } from 'components/form/constants'
import { twColor } from '@/utils/style'
import { Spinner } from 'components/spinner'

export type ItemListProps = IBaseComponentProps & {
  items: IInputOption[];
  itemStyle?: CSSProperties;
  maxVisibleItems?: number
  onItemClick?: (item: IInputOption, index: number) => void | Promise<void>
  expanded: boolean
  interpolation: number
  anchor: Ref
  handleSearchInput: (ev: Event) => void | Promise<void>
  state: SearchDropdownState
  searchOptions?: InputSearchOptions
  options?: Omit<FormFieldOptions, 'items'> & {
    items?: IInputOption[]
  }
  disabled?: boolean
  fieldRef?: FormFieldRef
  addItem: (item: IInputOption) => void
  setLoading: (loading: boolean) => void
  isLoading: boolean;
  selectedIndex: number;
  size?: string;
  minWidth?: string;
  config?: SearchDropdownConfig;
}

type ItemProps = IBaseComponentProps & {
  item: IInputOption;
  layout?: string;
  selected: boolean;
  size?: string;
  dropdownIsExpanded?: boolean;
  optimize?: boolean;
  itemStyle?: CSSProperties;
  small?: boolean;
  config?: SearchDropdownConfig;
}

const clazz = defineVariants({
  slots: {
    base: 'px-4 text-gray-900 gap-4 hover:text-primary-500 hover:bg-findex-50',
  },
  variants: {
    color: {
      primary: {
        base: {
          class: 'text-primary-500 placeholder-primary-500'
        }
      },
      gray: {
        base: {
          class: 'bg-gray-100'
        }
      },
      light: {
        base: {
          class: 'text-gray-600'
        }
      }
    },
    disabled: {
      true: {
        base: {
          class: 'text-gray-100'
        }
      }
    },
    selected: {
      true: {
        base: {
          class: 'text-primary-500 bg-findex-50'
        }
      }
    },
    placeholder: {
      true: {
        base: {
          class: 'text-gray-500'
        }
      }
    },
    size: {
      xs: {
        base: (props) => {
          return {
            class: 'text-sm font-medium pl-2 px-3 pr-3',
            style: props.hasIconRight ? {
              justifyContent: 'center',
              alignItems: 'center',
              alignContent: 'center',
              justifyItems: 'center',
              gap: '0.5rem',
              fontWeight: '500'
            } : {}
          }
        }
      },
    },
    dropdownIsExpanded: {
      true: {
        base: {
          class: 'bg-white'
        }
      }
    }
  }
})

export const Item = defineComponent<ItemProps>({
  setup(props, ctx) {
    const elref = ref()
    const visible = useElementVisibility(elref);

    const isPlaceholder = computed(() => props.item.value === "")
    console.log("item", props.item)
    const layout = computed(() => {
      if (props.layout) return props.layout
      if (props.small) return 'max-content';

      const count =
        Number(!!props.item.iconLeft) +
        Number(!!props.item.iconRight) +
        Number(!!props.item.bodyLeft) +
        Number(!!props.item.bodyRight)

      if (count >= 2) return `max-content auto max-content`
      if (props.item.iconLeft || props.item.bodyLeft) return `max-content auto`
      if (props.item.iconRight || props.item.bodyRight) return `auto max-content`
      return 'auto'
    });


    return () => {
      const classes = clazz({
        hasIconRight: !!props.item.iconRight,
        color: props.variant as any,
        disabled: !!props.disabled,
        selected: !!props.selected,
        size: props.size as any,
        dropdownIsExpanded: props.dropdownIsExpanded,
        placeholder: isPlaceholder.value
      });
      const item = props.item
      const text = item.label || item.name || toString(item.value)
      //   const layout = props.layout || (item.iconLeft ? 'max-content auto' : 'auto')
      return (
        <div
          ref={elref}
          class={`${classes.value.base.class} flex py-3 w-full user-select-none`}
          style={[
            {
              display: 'grid',
              gridTemplateColumns: layout.value,
              cursor: props.item.disabled ? 'not-allowed' : 'pointer',
            },
            classes.value.base.style || {}, props.itemStyle || {}, props.small ? {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
            } : {},
            props.item.disabled ? {
              color: twColor('gray-300')
            } : {}
          ]}
        >
          {props.item.iconLeft && (visible.value || props.optimize === false) && (
            <Centered>
              <Icon src={props.item.iconLeft} />
            </Centered>
          )}
          {props.item.bodyLeft && (
            <Centered>
              <props.item.bodyLeft />
            </Centered>
          )}
          {props.item.body ? (
            <props.item.body />
          ) : ctx.slots.default ? (
            ctx.slots.default()
          ) : (
            props.small ? null : <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                ...(props.size === 'xs' ? {
                  fontWeight: '500'
                } : {})
              }}
            >
              {text}
            </div>
          )}
          {props.item.bodyRight && (
            <Centered>
              <props.item.bodyRight />
            </Centered>
          )}
          {props.item.iconRight && (
            <Centered>
              <Icon src={props.item.iconRight} />
            </Centered>
          )}
        </div>
      )
    }
  }
})

Item.props = ['item', 'variant', 'selected', 'size', 'dropdownIsExpanded', 'optimize', 'itemStyle', 'small', 'config']

const ItemList = defineComponent<ItemListProps>({
  setup(props) {
    const maxVisibleItems = computed(
      () => props.maxVisibleItems || Math.min(MAX_VISIBLE_ITEMS, props.items.length)
    )

    const handleItemClick = (item: IInputOption, index: number) => {
      if (props.onItemClick) {
        props.onItemClick(item, index)
      }
    }

    const anchorBounding = useElementBounding(props.anchor)
    const windowSize = useWindowSize()
    const selfRef = ref()

    const selfBounding = useElementBounding(selfRef)

    const outsidePx = computed(() => {
      anchorBounding.update();
      const anchorY = anchorBounding.y.value
      return Math.max(
        0,
        anchorY +
        selfBounding.height.value -
        (windowSize.height.value - FORM_INPUT_HEIGHT_PX)
      )
    });

    const targetPosition = computed(() => {
      selfBounding.update();
      const interp = 1.0 - props.interpolation;

      const xOffset = props.config?.itemListPlacement === 'left' ? ((-selfBounding.width.value) + anchorBounding.width.value) : 0;

      const x = anchorBounding.x.value + xOffset;
      const anchorY = anchorBounding.y.value
      const outside = outsidePx.value
      const y = anchorY + ((FORM_INPUT_HEIGHT_PX + 0.5 - interp) - outside)
      return VEC2(x, y)
    });

    const position = computed(() => {
      selfBounding.update();
      const p = targetPosition.value;

      const overflowRight = Math.max(0, (p.x + selfBounding.width.value + 16) - windowSize.width.value);

      return p.add(VEC2(-overflowRight, 0));
    })

    const opacity = computed(() => {
      return props.interpolation * 100 + '%'
    })

    const isEmpty = computed(() => props.items.length <= 0)
    const emptyStateVisible = computed(() => isEmpty.value && !props.state.loading && !props.isLoading);

    const addButtonVisible = computed(() => !!props.options && !!props.options.add && !!props.options.add.createItem);

    const emptyStateSubtitle = computed(() => {
      if (!props.state.searchString || props.state.searchString.length <= 0) {
        if (props.searchOptions?.emptyStateText) return props.searchOptions.emptyStateText;
        return 'No results';
      }
      return `No results for ${props.state.searchString}`;
    })

    // const EmptyStateComponent = props.searchOptions?.emptyState ||


    const handleClickAddButton = async () => {
      const options = props.options
      if (!options) return
      const add = options.add
      if (!add) return
      props.setLoading(true)
      const item = await add.createItem(props.state.searchString || '')

      if (item) {
        props.addItem(item)
      }
      props.setLoading(false)
    }

    return () => {
      return (
        <div
          ref={selfRef}
          style={{
            width: anchorBounding.width.value + 'px',
            overflow: emptyStateVisible.value ? 'visible' : 'hidden',
            position: 'fixed',
            opacity: opacity.value,
            top: position.value.y + 'px',
            left: position.value.x + 'px',
            background: 'white',
            zIndex: 101,
            height: `auto`,
            ...(props.minWidth ? {
              minWidth: props.minWidth
            } : {}),
            ...(props.interpolation <= 0.00001
              ? {
                display: 'none'
              }
              : {}),
            ...(!props.expanded
              ? {
                pointerEvents: 'none'
              }
              : {})
          }}
          class="shadow-lg rounded-md "
        >
          {props.isLoading && (
            <div class="py-12">
              <Spinner />
            </div>
          )}
          {!props.isLoading && !emptyStateVisible.value && (
            <div
              class="pretty-scrollbar"
              style={{
                height: `${maxVisibleItems.value * (FORM_INPUT_HEIGHT_PX + 24)}px`,
                overflowY: 'auto',
                background: 'white'
              }}
            >
              {props.items.map((it, i) => {
                const selected = (i === props.selectedIndex);
                return (
                  <Item
                    selected={selected}
                    config={props.config}
                    variant={props.variant}
                    itemStyle={props.itemStyle}
                    size={props.size}
                    onClick={() => handleItemClick(it, i)}
                    dropdownIsExpanded={props.expanded}
                    item={it}
                    key={`item-list-item-${i}-${it.name || it.label || i}`}
                  />
                )
              })}
            </div>
          )}
          {!props.isLoading && emptyStateVisible.value && (
            <div class="py-10">
              <EmptyState
                size="s"
                iconContainer={{
                  style: {
                    width: '2rem',
                    height: '2rem'
                  }
                }}
                icon={{
                  src: SearchIcon,
                  stroke: 'currentColor',
                  size: '1rem'
                }}
                subtitle={emptyStateSubtitle.value}
                action={addButtonVisible.value ? () => {
                  return (
                    <Button
                      onClick={handleClickAddButton}
                      icon={{
                        src: AddIcon,
                        fill: 'currentColor',
                        size: 14
                      }}
                    >
                      Create
                    </Button>
                  )
                } : undefined}
              />
            </div>
          )}
        </div>
      )
    }
  }
})

ItemList.props = [
  'items',
  'size',
  'onItemClick',
  'layout',
  'variant',
  'expanded',
  'interpolation',
  'anchor',
  'handleSearchInput',
  'state',
  'maxVisibleItems',
  'searchOptions',
  'fieldRef',
  'disabled',
  'addItem',
  'options',
  'setLoading',
  'isLoading',
  'selectedIndex',
  'itemStyle',
  'minWidth',
  'config'
]

export default ItemList
